import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { Table, type Column } from "@/components/ui/Table";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { TextInput, SelectInput } from "@/components/ui/Field";
import { DataTableScrollShell } from "@/components/dataTable/DataTableScrollShell";
import { SortableHeader, TableSortResetBar, sortRowsByKey, useTableSort } from "@/components/dataTable";
import { YearFilterBar } from "../../components/YearFilterBar";
import { StandardMaterialDialog } from "../../components/StandardMaterialDialog";
import {
  copyOrdnerSelected,
  createJahr,
  createOrdner,
  createOrdnerStandardMaterial,
  deleteOrdner,
  deleteOrdnerStandardMaterial,
  getOrdner,
  historischAuftragTemplateDownloadUrl,
  historischOrdnerTemplateDownloadUrl,
  importHistorischAuftragExcel,
  importHistorischOrdnerExcel,
  listJahre,
  listOrdner,
  listOrdnerJahre,
  listOrdnerStandardMaterials,
  listKunden,
  updateOrdner,
  updateOrdnerStandardMaterial,
  type KundeOut,
  type OrdnerOut,
} from "../../lib/settingsApi";
import { getHeroBranches, getHeroMeasures, getHeroProjectTypes, getHeroProjectSteps } from "@/lib/projects";
import type { HeroBranchLookup, HeroMeasureLookup, HeroProjectTypeLookup, HeroProjectStepLookup } from "@/lib/projects";
import { useRealtimeSync } from "@/lib/useRealtime";

interface OrdnerFormState {
  kundeId: string;
  suffix: string;
  nummer: string;
  beschreibung: string;
  branchId: string;
  measureId: string;
  projectTypeId: string;
  projectStepId: string;
  askCommissionAfterCreate: boolean;
  allowSchadensmeldungOnCreate: boolean;
}

const EMPTY_FORM: OrdnerFormState = {
  kundeId: "",
  suffix: "",
  nummer: "",
  beschreibung: "",
  branchId: "",
  measureId: "",
  projectTypeId: "",
  projectStepId: "",
  askCommissionAfterCreate: false,
  allowSchadensmeldungOnCreate: false,
};

export function OrdnerSection() {
  const currentYear = new Date().getFullYear();
  const [years, setYears] = useState<number[]>([]);
  const [historicalYears, setHistoricalYears] = useState<Set<number>>(new Set());
  const [activeYear, setActiveYear] = useState(currentYear);
  const [rows, setRows] = useState<OrdnerOut[]>([]);
  const [kunden, setKunden] = useState<KundeOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<OrdnerFormState>(EMPTY_FORM);
  const [formBaseline, setFormBaseline] = useState<OrdnerFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [materialOrdner, setMaterialOrdner] = useState<OrdnerOut | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySourceYear, setCopySourceYear] = useState<number | null>(null);
  const [copySelected, setCopySelected] = useState<Set<number>>(new Set());
  const [copySourceRows, setCopySourceRows] = useState<OrdnerOut[]>([]);
  const [yearDialogOpen, setYearDialogOpen] = useState(false);
  const [newYear, setNewYear] = useState(String(currentYear - 1));
  const [newYearHistorisch, setNewYearHistorisch] = useState(true);
  const [yearError, setYearError] = useState<string | null>(null);
  const [importInfo, setImportInfo] = useState<string | null>(null);
  const ordnerFileRef = useRef<HTMLInputElement>(null);
  const auftragFileRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const [branches, setBranches] = useState<HeroBranchLookup[]>([]);
  const [measures, setMeasures] = useState<HeroMeasureLookup[]>([]);
  const [projectTypes, setProjectTypes] = useState<HeroProjectTypeLookup[]>([]);
  const [projectSteps, setProjectSteps] = useState<HeroProjectStepLookup[]>([]);
  const { sort: copySort, toggle: toggleCopySort, reset: resetCopySort } = useTableSort();
  const sortedCopySourceRows = useMemo(
    () =>
      sortRowsByKey(copySourceRows, copySort, (row, key) =>
        key === "nummer" ? row.nummer : row.beschreibung,
      ),
    [copySort, copySourceRows],
  );

  const isHistorisch = historicalYears.has(activeYear);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const [jahreMeta, jahreNums, ordner, kundenList] = await Promise.all([
        listJahre(),
        listOrdnerJahre(),
        listOrdner(activeYear),
        listKunden(),
      ]);
      const hist = new Set(jahreMeta.filter((j) => j.is_historisch).map((j) => j.jahr));
      const mergedYears = Array.from(
        new Set([...jahreNums, ...jahreMeta.map((j) => j.jahr), activeYear]),
      ).sort((a, b) => b - a);
      setHistoricalYears(hist);
      setYears(mergedYears.length ? mergedYears : [activeYear]);
      setRows(ordner);
      setKunden(kundenList.sort((a, b) => a.code - b.code));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ordner konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [activeYear]);

  useEffect(() => {
    reload();
  }, [reload]);

  useRealtimeSync({
    domains: ["ordner", "kunden"],
    paused: formOpen || copyOpen || materialOrdner != null || yearDialogOpen,
    onChange: () => void reload({ silent: true }),
  });

  useEffect(() => {
    (async () => {
      const [b, m, pt] = await Promise.all([
        getHeroBranches().catch(() => []),
        getHeroMeasures().catch(() => []),
        getHeroProjectTypes().catch(() => []),
      ]);
      setBranches(b || []);
      setMeasures(m || []);
      setProjectTypes(pt || []);
    })();
  }, []);

  useEffect(() => {
    const typeId = parseInt(form.projectTypeId, 10);
    if (!Number.isFinite(typeId)) {
      setProjectSteps([]);
      return;
    }
    getHeroProjectSteps(typeId)
      .then((s) => setProjectSteps(s || []))
      .catch(() => setProjectSteps([]));
  }, [form.projectTypeId]);

  const labelMap = useMemo(() => {
    const b = new Map(branches.map((x) => [x.id, x.name]));
    const m = new Map(measures.map((x) => [x.id, x.name || x.short || ""]));
    const pt = new Map(projectTypes.map((x) => [x.id, x.name]));
    return { b, m, pt };
  }, [branches, measures, projectTypes]);

  function openCreate() {
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormBaseline(EMPTY_FORM);
    setFormError(null);
    setFormOpen(true);
  }

  async function openEdit(id: number) {
    setFormError(null);
    try {
      const data = await getOrdner(id);
      const parts = (data.nummer || "").split("-");
      const suffix = parts.length > 1 ? parts.slice(1).join("-") : "";
      setEditId(id);
      const loadedForm: OrdnerFormState = {
        kundeId: data.kunde_id != null ? String(data.kunde_id) : "",
        suffix,
        nummer: data.nummer || "",
        beschreibung: data.beschreibung || "",
        branchId: data.branch_id != null ? String(data.branch_id) : "",
        measureId: data.measure_id != null ? String(data.measure_id) : "",
        projectTypeId: data.project_type_id != null ? String(data.project_type_id) : "",
        projectStepId: data.project_step_id != null ? String(data.project_step_id) : "",
        askCommissionAfterCreate: Boolean(data.ask_commission_after_create),
        allowSchadensmeldungOnCreate: Boolean(data.allow_schadensmeldung_on_create),
      };
      setForm(loadedForm);
      setFormBaseline(loadedForm);
      setFormOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ordner konnte nicht geladen werden.");
    }
  }

  const formDirty = useMemo(() => hasFormChanges(form, formBaseline), [form, formBaseline]);

  function closeForm() {
    setFormOpen(false);
  }

  async function handleSave() {
    if (!form.beschreibung.trim()) {
      setFormError("Beschreibung ist Pflichtfeld.");
      return;
    }

    let nummer = "";
    if (isHistorisch) {
      nummer = form.nummer.trim();
      if (!nummer) {
        setFormError("Nummer ist Pflichtfeld.");
        return;
      }
    } else {
      const kunde = kunden.find((k) => String(k.id) === form.kundeId);
      if (!kunde) {
        setFormError("Bitte einen Kunden wählen.");
        return;
      }
      if (!form.suffix.trim()) {
        setFormError("Nummer und Beschreibung sind Pflichtfelder.");
        return;
      }
      nummer = `${kunde.code}-${form.suffix.trim()}`;
    }

    const body = {
      nummer,
      beschreibung: form.beschreibung.trim(),
      jahr: activeYear,
      branch_id: form.branchId ? parseInt(form.branchId, 10) : null,
      measure_id: form.measureId ? parseInt(form.measureId, 10) : null,
      project_type_id: form.projectTypeId ? parseInt(form.projectTypeId, 10) : null,
      project_step_id: form.projectStepId ? parseInt(form.projectStepId, 10) : null,
      ask_commission_after_create: form.askCommissionAfterCreate,
      allow_schadensmeldung_on_create: form.allowSchadensmeldungOnCreate,
    };
    setSaving(true);
    setFormError(null);
    try {
      if (editId) await updateOrdner(editId, body);
      else await createOrdner(body);
      setFormOpen(false);
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row: OrdnerOut) {
    if (!window.confirm(`Ordner „${row.nummer}" wirklich löschen?`)) return;
    try {
      await deleteOrdner(row.id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  async function openCopyFromPrev() {
    const src = activeYear - 1;
    setCopySourceYear(src);
    setCopySelected(new Set());
    try {
      const sourceRows = await listOrdner(src);
      setCopySourceRows(sourceRows);
      setCopyOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Quelljahre konnten nicht geladen werden.");
    }
  }

  async function handleCopySave() {
    if (!copySourceYear || copySelected.size === 0) return;
    setSaving(true);
    try {
      await copyOrdnerSelected(copySourceYear, activeYear, [...copySelected]);
      setCopyOpen(false);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kopieren fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateYear() {
    const jahr = parseInt(newYear, 10);
    if (!Number.isFinite(jahr) || jahr < 1900 || jahr > 2100) {
      setYearError("Bitte ein gültiges Jahr eingeben.");
      return;
    }
    setSaving(true);
    setYearError(null);
    try {
      await createJahr({ jahr, is_historisch: newYearHistorisch });
      setYearDialogOpen(false);
      setActiveYear(jahr);
      await reload();
    } catch (err) {
      setYearError(err instanceof Error ? err.message : "Jahr konnte nicht angelegt werden.");
    } finally {
      setSaving(false);
    }
  }

  async function handleOrdnerExcelUpload(file: File | null) {
    if (!file || !isHistorisch) return;
    setImportInfo(null);
    try {
      const result = (await importHistorischOrdnerExcel(activeYear, file)) as {
        ok?: number;
        fail?: number;
      };
      setImportInfo(`Ordner-Import: ${result.ok ?? 0} ok, ${result.fail ?? 0} Fehler`);
      await reload({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ordner-Import fehlgeschlagen.");
    } finally {
      if (ordnerFileRef.current) ordnerFileRef.current.value = "";
    }
  }

  async function handleAuftragExcelUpload(ordnerId: number, file: File | null) {
    if (!file) return;
    setImportInfo(null);
    try {
      const result = (await importHistorischAuftragExcel(ordnerId, file)) as {
        ok?: number;
        fail?: number;
      };
      setImportInfo(`Auftrags-Import: ${result.ok ?? 0} ok, ${result.fail ?? 0} Fehler`);
      await reload({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Auftrags-Import fehlgeschlagen.");
    } finally {
      const ref = auftragFileRefs.current[ordnerId];
      if (ref) ref.value = "";
    }
  }

  const tableColumns = useMemo<Column<OrdnerOut>[]>(() => {
    const cols: Column<OrdnerOut>[] = [
      {
        key: "nummer",
        header: "Nummer",
        sortable: true,
        sortValue: (r) => r.nummer,
        render: (r) => r.nummer,
      },
      {
        key: "beschreibung",
        header: "Beschreibung",
        sortable: true,
        sortValue: (r) => r.beschreibung,
        render: (r) => r.beschreibung,
      },
    ];
    if (!isHistorisch) {
      cols.push({
        key: "kunde",
        header: "Kunde",
        sortable: true,
        sortValue: (r) => r.kunden_name || "",
        render: (r) => r.kunden_name || "—",
      });
      cols.push(
        {
          key: "branch",
          header: "Niederlassung",
          sortable: true,
          sortValue: (r) =>
            r.branch_id != null
              ? labelMap.b.get(r.branch_id) || r.branch_name || ""
              : "",
          render: (r) =>
            r.branch_id != null
              ? labelMap.b.get(r.branch_id) || r.branch_name || "—"
              : "—",
        },
        {
          key: "measure",
          header: "Gewerk",
          sortable: true,
          sortValue: (r) =>
            r.measure_id != null
              ? labelMap.m.get(r.measure_id) || r.measure_name || ""
              : "",
          render: (r) =>
            r.measure_id != null
              ? labelMap.m.get(r.measure_id) || r.measure_name || "—"
              : "—",
        },
        {
          key: "type",
          header: "Projekttyp",
          sortable: true,
          sortValue: (r) =>
            r.project_type_id != null
              ? labelMap.pt.get(r.project_type_id) || r.project_type_name || ""
              : "",
          render: (r) =>
            r.project_type_id != null
              ? labelMap.pt.get(r.project_type_id) || r.project_type_name || "—"
              : "—",
        },
      );
    }
    cols.push({
      key: "actions",
      header: "",
      render: (r) => (
        <div className="flex flex-wrap justify-end gap-1">
          {isHistorisch ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() =>
                  window.open(historischAuftragTemplateDownloadUrl(r.id), "_blank", "noopener")
                }
              >
                Auftrags-Vorlage
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => auftragFileRefs.current[r.id]?.click()}
              >
                Aufträge hochladen
              </Button>
              <input
                ref={(el) => {
                  auftragFileRefs.current[r.id] = el;
                }}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) =>
                  void handleAuftragExcelUpload(r.id, e.target.files?.[0] ?? null)
                }
              />
            </>
          ) : null}
          <Button type="button" size="sm" variant="secondary" onClick={() => openEdit(r.id)}>
            Bearbeiten
          </Button>
          {!isHistorisch ? (
            <Button type="button" size="sm" variant="secondary" onClick={() => setMaterialOrdner(r)}>
              Material
            </Button>
          ) : null}
          <Button type="button" size="sm" variant="danger" onClick={() => handleDelete(r)}>
            Löschen
          </Button>
        </div>
      ),
    });
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers use stable reload/activeYear
  }, [isHistorisch, labelMap]);

  return (
    <Card
      title="Vorhandene Ordner"
      description={
        isHistorisch
          ? `Historische Ordner für ${activeYear} (nur Ordnernummer, ohne Kundenverknüpfung).`
          : "Projekt-Ordner je Jahr verwalten."
      }
      actions={
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={() => setYearDialogOpen(true)}>
            Neues Jahr
          </Button>
          {isHistorisch ? (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  window.open(historischOrdnerTemplateDownloadUrl(activeYear), "_blank", "noopener")
                }
              >
                Ordner-Vorlage
              </Button>
              <Button type="button" variant="secondary" onClick={() => ordnerFileRef.current?.click()}>
                Ordner hochladen
              </Button>
              <input
                ref={ordnerFileRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => void handleOrdnerExcelUpload(e.target.files?.[0] ?? null)}
              />
            </>
          ) : (
            <Button type="button" variant="secondary" onClick={openCopyFromPrev}>
              Aus {activeYear - 1} übernehmen
            </Button>
          )}
          <Button type="button" onClick={openCreate}>
            Neu
          </Button>
        </div>
      }
    >
      <div className="mb-4">
        <YearFilterBar
          years={years}
          activeYear={activeYear}
          onChange={setActiveYear}
          historicalYears={historicalYears}
        />
      </div>
      {isHistorisch ? (
        <Alert variant="info" title="Historisches Jahr" className="mb-3">
          Ordner nur mit Nummer und Beschreibung. Kunden und Rechnungsnummern gehören in die
          Auftrags-Excel-Dateien je Ordner.
        </Alert>
      ) : null}
      {importInfo ? (
        <Alert variant="success" title="Import" className="mb-3">
          {importInfo}
        </Alert>
      ) : null}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lädt …
        </div>
      )}
      {error && (
        <Alert variant="error" title="Fehler" className="mb-3">
          {error}
        </Alert>
      )}
      {!loading && (
        <Table<OrdnerOut>
          rowKey={(r) => r.id}
          rows={rows}
          stickyEndColumnCount={1}
          emptyText={
            <div className="space-y-2">
              <p>Keine Einträge für {activeYear}.</p>
              {!isHistorisch ? (
                <Button type="button" variant="secondary" size="sm" onClick={openCopyFromPrev}>
                  Ordner aus {activeYear - 1} übernehmen
                </Button>
              ) : null}
            </div>
          }
          columns={tableColumns}
        />
      )}

      <Dialog
        open={yearDialogOpen}
        onClose={() => setYearDialogOpen(false)}
        title="Neues Jahr anlegen"
        size="md"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setYearDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" onClick={() => void handleCreateYear()} loading={saving}>
              Anlegen
            </Button>
          </>
        }
      >
        {yearError && (
          <Alert variant="error" title="Fehler" className="mb-3">
            {yearError}
          </Alert>
        )}
        <div className="space-y-3">
          <TextInput
            label="Jahr"
            type="number"
            min={1900}
            max={2100}
            required
            value={newYear}
            onChange={(e) => setNewYear(e.target.value)}
          />
          <label className="flex items-center gap-2 text-sm text-label">
            <input
              type="checkbox"
              checked={newYearHistorisch}
              onChange={(e) => setNewYearHistorisch(e.target.checked)}
            />
            Historische Daten
          </label>
          <p className="text-sm text-label-secondary">
            Bei historischen Jahren gelten reine Ordnernummern (ohne Kundencode) und Excel-Import für
            Ordner und Aufträge.
          </p>
        </div>
      </Dialog>

      <Dialog
        open={formOpen}
        onClose={closeForm}
        dirty={formDirty}
        title={editId ? "Ordner bearbeiten" : "Neuen Ordner anlegen"}
        size="lg"
        footer={
          <OrdnerFormFooter
            onClose={closeForm}
            onSave={() => void handleSave()}
            saving={saving}
            editId={editId}
          />
        }
      >
        {formError && (
          <Alert variant="error" title="Fehler" className="mb-3">
            {formError}
          </Alert>
        )}
        <div className="space-y-3">
          {isHistorisch ? (
            <TextInput
              label="Nummer"
              required
              value={form.nummer}
              onChange={(e) => setForm((f) => ({ ...f, nummer: e.target.value }))}
              hint="Historische Ordnernummer ohne Kundencode"
            />
          ) : (
            <>
              <SelectInput
                label="Kunde"
                required
                value={form.kundeId}
                onChange={(e) => setForm((f) => ({ ...f, kundeId: e.target.value }))}
                disabled={!!editId}
              >
                <option value="">— Kunde wählen —</option>
                {kunden.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.code} – {k.name}
                  </option>
                ))}
              </SelectInput>
              <TextInput
                label="Nummer (Suffix)"
                type="number"
                min={1}
                required
                value={form.suffix}
                onChange={(e) => setForm((f) => ({ ...f, suffix: e.target.value }))}
                hint={
                  form.kundeId
                    ? `Vorschau: ${kunden.find((k) => String(k.id) === form.kundeId)?.code ?? "?"}-${form.suffix || "…"}`
                    : undefined
                }
              />
            </>
          )}
          <TextInput
            label="Beschreibung"
            required
            value={form.beschreibung}
            onChange={(e) => setForm((f) => ({ ...f, beschreibung: e.target.value }))}
          />
          {!isHistorisch ? (
            <>
              <SelectInput
                label="Niederlassung (optional)"
                value={form.branchId}
                onChange={(e) => setForm((f) => ({ ...f, branchId: e.target.value }))}
              >
                <option value="">— Niederlassung wählen —</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </SelectInput>
              <SelectInput
                label="Gewerk (optional)"
                value={form.measureId}
                onChange={(e) => setForm((f) => ({ ...f, measureId: e.target.value }))}
              >
                <option value="">— Gewerk wählen —</option>
                {measures.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name || m.short}
                  </option>
                ))}
              </SelectInput>
              <SelectInput
                label="Projekttyp (optional)"
                value={form.projectTypeId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, projectTypeId: e.target.value, projectStepId: "" }))
                }
              >
                <option value="">— Projekttyp wählen —</option>
                {projectTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </SelectInput>
              <SelectInput
                label="Status-Schritt (optional)"
                value={form.projectStepId}
                onChange={(e) => setForm((f) => ({ ...f, projectStepId: e.target.value }))}
                disabled={!form.projectTypeId}
              >
                <option value="">— Schritt wählen —</option>
                {projectSteps.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </SelectInput>
            </>
          ) : null}
          <label className="flex items-start gap-2 text-sm text-label">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.askCommissionAfterCreate}
              onChange={(e) =>
                setForm((f) => ({ ...f, askCommissionAfterCreate: e.target.checked }))
              }
            />
            <span>
              Bei Projekterstellung fragen, ob beauftragt wurde
              <span className="mt-0.5 block text-xs text-label-secondary">
                Bei „Ja“ öffnet sich danach der Dialog „Mündlich beauftragt“.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-label">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.allowSchadensmeldungOnCreate}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  allowSchadensmeldungOnCreate: e.target.checked,
                }))
              }
            />
            <span>
              Bei Projekterstellung Schadensmeldung / FM-Auftrag hochladen
              <span className="mt-0.5 block text-xs text-label-secondary">
                Mit Upload entfällt die Beauftragungsfrage – das Projekt wird
                direkt als beauftragt gesetzt.
              </span>
            </span>
          </label>
        </div>
      </Dialog>

      <Dialog
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        dirty={copySelected.size > 0}
        title={`Ordner aus ${copySourceYear} übernehmen`}
        size="lg"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setCopyOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" onClick={handleCopySave} loading={saving} disabled={copySelected.size === 0}>
              Speichern
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-label-secondary">
          Hake die Ordner an, die im Jahr {activeYear} angelegt werden sollen.
        </p>
        <TableSortResetBar visible={copySort != null} onReset={resetCopySort} />
        <DataTableScrollShell
          className="max-h-[50vh]"
          stickyHeader={false}
          syncKey={`copy-ordner|${copySourceRows.length}`}
          head={
            <tr>
              <th className="eh-data-th px-3 py-2 text-left">Auswahl</th>
              <th className="eh-data-th px-3 py-2 text-left">
                <SortableHeader label="Nummer" columnKey="nummer" sort={copySort} onSort={toggleCopySort} />
              </th>
              <th className="eh-data-th px-3 py-2 text-left">
                <SortableHeader label="Beschreibung" columnKey="beschreibung" sort={copySort} onSort={toggleCopySort} />
              </th>
            </tr>
          }
        >
          <tbody className="divide-y divide-separator">
            {sortedCopySourceRows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={copySelected.has(r.id)}
                    onChange={(e) => {
                      setCopySelected((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(r.id);
                        else next.delete(r.id);
                        return next;
                      });
                    }}
                  />
                </td>
                <td className="px-3 py-2">{r.nummer}</td>
                <td className="px-3 py-2">{r.beschreibung}</td>
              </tr>
            ))}
          </tbody>
        </DataTableScrollShell>
      </Dialog>

      {materialOrdner && (
        <StandardMaterialDialog
          open={!!materialOrdner}
          onClose={() => setMaterialOrdner(null)}
          title={`Standardmaterialien – ${materialOrdner.nummer} ${materialOrdner.beschreibung}`}
          api={{
            list: () => listOrdnerStandardMaterials(materialOrdner.id),
            create: (body) => createOrdnerStandardMaterial(materialOrdner.id, body),
            update: (id, body) => updateOrdnerStandardMaterial(materialOrdner.id, id, body),
            remove: (id) => deleteOrdnerStandardMaterial(materialOrdner.id, id),
          }}
        />
      )}
    </Card>
  );
}

function OrdnerFormFooter({
  onClose,
  onSave,
  saving,
  editId,
}: {
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  editId: number | null;
}) {
  const requestClose = useDialogRequestClose(onClose);
  return (
    <>
      <Button type="button" variant="secondary" onClick={requestClose}>
        Abbrechen
      </Button>
      <Button type="button" onClick={onSave} loading={saving}>
        {editId ? "Aktualisieren" : "Speichern"}
      </Button>
    </>
  );
}
